import { useQueries, queryOptions } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import type { SeriesAreaOptions } from "highcharts";

import type { SimplifiedSeries } from "@similarweb/pro-logic-components";

import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import { fetchCategoryData } from "app/pages/industry-analysis/common/queries/shared/industryAnalysisQueryUtils";
import { timeGranularity } from "app/components/widget/widget-utilities/time-granularity";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { MARKET_SIZE_ENDPOINTS } from "../consts";
import type { AudienceTableRow, MarketSizeDataPoint, MarketSizeDataResponse, MarketSizeScoreResponse } from "../types";
import { getFromTo } from "../utils";

const DEFAULT_QUERY_PARAMS = {
    timeGranularity: timeGranularity.monthly,
};

// Helper functions for audience data transformation
const getUniqueVisitors = (totalArr: MarketSizeDataPoint[]): number =>
    totalArr.length ? totalArr[totalArr.length - 1].Value : 0;

const getTrend = (totalArr: MarketSizeDataPoint[]): SimplifiedSeries<SeriesAreaOptions> => [
    { data: totalArr.map((item) => item.Value) },
];

const getChangeValue = (previousValue: number, currentValue: number): number => {
    if (!currentValue || !previousValue) {
        return 0;
    }

    return (currentValue - previousValue) / previousValue;
};

type UseAudienceDataResult = {
    data: AudienceTableRow[];
    isLoading: boolean;
    isError: boolean;
    error: unknown;
};

export const useAudienceComparisonData = (categories: ICategoryComparison[]): UseAudienceDataResult => {
    const { params } = useCurrentStateAndParams();
    const { country, comparedDuration, includeSubdomains = true, webSource } = params;
    const durations = DurationService.getDurationData(BasicDurations.LAST_MONTH, comparedDuration);
    const { isWindow, compareFrom, compareTo } = durations.forAPI;

    const result = useQueries({
        queries: categories.map(({ category }) => {
            const { forDisplayApi, categoryHash, forApi, id } = category;
            const { from, to } = getFromTo();
            const uniqueParams = {
                from,
                to,
                category: forDisplayApi,
                categoryHash,
                country,
                isWindow,
                keys: forApi,
                webSource,
                includeSubdomains,
                ...DEFAULT_QUERY_PARAMS,
            };
            const scoreParams = {
                ...uniqueParams,
                compareFrom,
                compareTo,
            };
            return queryOptions({
                queryKey: ["marketSize", scoreParams],
                queryFn: async () => {
                    const [uniqueData, scoreData] = await Promise.all([
                        fetchCategoryData(MARKET_SIZE_ENDPOINTS.unique, category, uniqueParams),
                        fetchCategoryData(MARKET_SIZE_ENDPOINTS.score, category, scoreParams),
                    ]);
                    return { unique: uniqueData, score: scoreData };
                },
            });
        }),
        combine: (results) => ({
            data: results,
            isLoading: results.some((r) => r.isLoading),
            isError: results.some((r) => r.isError),
            error: results.find((r) => r.error)?.error || null,
        }),
    });

    const transformedTableData: AudienceTableRow[] = categories.map((cat, i) => {
        const categoryIdForApi = cat.category.forApi;
        const resultData = result.data[i]?.data;
        const uniqueData = resultData?.unique as MarketSizeDataResponse | undefined;
        const scoreData = resultData?.score as MarketSizeScoreResponse | undefined;
        const totalArr = uniqueData?.Data?.[categoryIdForApi]?.Total?.[0] ?? [];
        const periodStartValue = totalArr.length > 3 ? totalArr[0].Value : 0;
        const periodEndValue = totalArr.length > 3 ? totalArr[totalArr.length - 1].Value : 0;
        return {
            color: cat.color,
            categoryName: cat.category.text || "",
            uniqueVisitors: {
                value: getUniqueVisitors(totalArr),
                share: 0, // will be set later, has to have max value calculated
            },
            periodChange: getChangeValue(periodStartValue, periodEndValue),
            trend: getTrend(totalArr),
            score: scoreData?.Score,
        };
    });

    const maxVisitors = Math.max(...transformedTableData.map((row) => row.uniqueVisitors.value));
    transformedTableData.forEach((row) => {
        row.uniqueVisitors.share = maxVisitors === 0 ? 0 : row.uniqueVisitors.value / maxVisitors;
    });

    return {
        data: transformedTableData,
        isLoading: result.isLoading,
        isError: result.isError,
        error: result.error,
    };
};
